// Phase 5 D — station bottom sheet.
//
// Mobile-only slide-up sheet that surfaces:
//   * Station identity — code + city + country.
//   * Coordinates in operator-friendly N/S · E/W format.
//   * Counters — `total_jobs · done_with_residual с измеренной |dR|`.
//   * Accuracy metrics — median + clickable «лучший» row that
//     deep-links to the source job's DetailScreen.
//   * Activity timestamp.
//   * Three action buttons:
//     - «Все задачи AMC4 →» — Feed pre-filtered (Phase 6.1 plumbing).
//     - «+ Запустить серию для AMC4» — opens BatchModal predfilled.
//
// Zero-jobs branch (`total_jobs == 0`) collapses the metrics block
// and the «Все задачи» button — only coordinates + the run-batch
// CTA remain.

import { Link, useNavigate } from 'react-router-dom';

import type { MapStation } from '../../api/types';
import { pluralize_jobs } from '../../lib/pluralize_ru';
import { formatConfig } from '../analytics/formatConfig';

import './StationBottomSheet.css';

/** Format a decimal-degrees coordinate pair with N/S · E/W
 *  hemisphere annotations. Matches the operator-readable shape
 *  the spec shows in §D.2 (e.g. `38.8031°N · 104.5246°W`). */
function formatCoordinates(lat: number, lng: number): string {
  const latHem = lat >= 0 ? 'N' : 'S';
  const lngHem = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}°${latHem} · ${Math.abs(lng).toFixed(4)}°${lngHem}`;
}

/** Pretty-print a UTC ISO timestamp as `DD.MM.YYYY`. Matches the
 *  series-card «активность» row format. */
function formatActivityDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

interface Props {
  station: MapStation;
  /** Backdrop click / drag-down handle / next-marker click — the
   *  parent (MapScreen) clears its selected-station state. */
  onClose: () => void;
  /** Drives the «+ Запустить серию» button. Parent (MapScreen)
   *  surfaces a BatchModal with the predfilled station code. */
  onRunBatch: (stationCode: string) => void;
}

export function StationBottomSheet({
  station,
  onClose,
  onRunBatch,
}: Props) {
  const navigate = useNavigate();
  const hasJobs = station.total_jobs > 0;

  // Feed deep-link mirrors Phase 6.1's existing handling: `/?station=…`
  // the FeedScreen reads via `useSearchParams()` and surfaces a
  // dismissible chip. We use the same path so the user gets the
  // familiar UX (and the «снять фильтр» chip).
  const feedHref = `/?station=${encodeURIComponent(station.code)}`;

  return (
    <>
      {/* Backdrop — click to dismiss. Lives outside the sheet so a
          click on the sheet content doesn't bubble up and close it. */}
      <button
        type="button"
        className="station-bottom-sheet-backdrop"
        onClick={onClose}
        aria-label="Закрыть карточку станции"
        data-testid="station-bottom-sheet-backdrop"
      />
      <aside
        className="station-bottom-sheet"
        role="dialog"
        aria-label={`Станция ${station.code}`}
        data-testid="station-bottom-sheet"
      >
        {/* Drag handle — visual affordance for the dismissable
            sheet. Tapping it also closes (parent click handler). */}
        <button
          type="button"
          className="station-bottom-sheet-handle"
          onClick={onClose}
          aria-label="Свернуть"
        />

        <header className="station-bottom-sheet-header">
          <span className="station-bottom-sheet-code">{station.code}</span>
          {(station.city || station.description) && (
            <span className="station-bottom-sheet-loc">
              · {station.city ?? station.description}
              {station.country_code ? `, ${station.country_code}` : ''}
            </span>
          )}
          {/* C-MAP — explicit, always-visible close control. The invisible
              backdrop still dismisses, but a Telegram Android user without a
              hardware Back that reaches the WebView (or who never learns the
              backdrop is tappable) needs a discoverable × affordance. `margin
              -left:auto` in CSS floats it to the header's right edge, clear of
              the station code / location. */}
          <button
            type="button"
            className="station-bottom-sheet-close"
            onClick={onClose}
            aria-label="Закрыть карточку станции"
            data-testid="station-bottom-sheet-close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div
          className="station-bottom-sheet-coords"
          data-testid="station-coords"
        >
          {formatCoordinates(station.latitude, station.longitude)}
        </div>

        {hasJobs && (
          <>
            <section className="station-bottom-sheet-section">
              <div className="station-bottom-sheet-label">всего задач</div>
              <div className="station-bottom-sheet-counts">
                <strong>{station.total_jobs}</strong>
                <span className="station-bottom-sheet-sep">·</span>
                <strong>{station.done_with_residual}</strong> с измеренной
                |dR|
              </div>
            </section>

            <section className="station-bottom-sheet-metrics">
              {station.median_dr_mm !== null && (
                <div className="station-bottom-sheet-metric-row">
                  <span className="station-bottom-sheet-label">
                    медиана |dR|
                  </span>
                  <span className="station-bottom-sheet-value">
                    {station.median_dr_mm.toFixed(1)} мм
                  </span>
                </div>
              )}
              {station.best_job_id !== null &&
                station.best_dr_mm !== null && (
                  <button
                    type="button"
                    className="station-bottom-sheet-best-link"
                    onClick={() =>
                      navigate(`/job/${station.best_job_id}`)
                    }
                    data-testid="station-best-link"
                  >
                    <span className="station-bottom-sheet-label">
                      лучший
                    </span>
                    <span className="station-bottom-sheet-best-target">
                      {station.code}
                      {station.best_config && (
                        <>
                          {' × '}
                          {formatConfig(station.best_config)}
                        </>
                      )}
                    </span>
                    <span className="station-bottom-sheet-value">
                      {station.best_dr_mm.toFixed(1)} мм
                    </span>
                    <span
                      className="station-bottom-sheet-arrow"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </button>
                )}
              <div className="station-bottom-sheet-metric-row">
                <span className="station-bottom-sheet-label">активность</span>
                <span className="station-bottom-sheet-value">
                  {station.active_now
                    ? 'идёт сейчас'
                    : `последняя ${formatActivityDate(station.last_activity_at)}`}
                </span>
              </div>
            </section>
          </>
        )}

        <footer className="station-bottom-sheet-actions">
          {hasJobs && (
            <Link
              to={feedHref}
              className="station-bottom-sheet-action-secondary"
              data-testid="station-feed-link"
            >
              Все задачи {station.code} →
            </Link>
          )}
          <button
            type="button"
            className="station-bottom-sheet-action-primary"
            onClick={() => onRunBatch(station.code)}
            data-testid="station-run-batch"
          >
            + Запустить серию для {station.code}
          </button>
        </footer>

        {hasJobs && (
          <div
            className="station-bottom-sheet-footer-note"
            data-testid="station-footer-note"
          >
            {pluralize_jobs(station.total_jobs)} всего в каталоге
          </div>
        )}
      </aside>
    </>
  );
}
